import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import '../styles/Employees.css';
import moment from 'moment';

// Import booking level images
import underbooked from '../assets/images/underbooked.png';
import booked from '../assets/images/booked.png';
import overbooked from '../assets/images/overbooked.png';

const Employees = () => {
    const [employees, setEmployees] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [activeTab, setActiveTab] = useState('employees');
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
    const [newEmployee, setNewEmployee] = useState({
        firstName: '',
        lastName: '',
        email: ''
    });
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [showSortOptions, setShowSortOptions] = useState(false);
    const [sortOrder, setSortOrder] = useState(null);
    const [showFilterOptions, setShowFilterOptions] = useState(false);
    const [visibleDepartments, setVisibleDepartments] = useState(new Set());

    // Add dark theme by default
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', 'dark');
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.sort-dropdown-container')) {
                setShowSortOptions(false);
            }
            if (!event.target.closest('.filter-dropdown-container')) {
                setShowFilterOptions(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [employeesRes, schedulesRes, companyRes] = await Promise.all([
                    api.get('/api/users/employees'),
                    api.get('/api/schedules'),
                    api.get('/api/companies/info')
                ]);

                // Create a mapping between user_ids and employee IDs
                const userIdToEmployeeId = {};
                employeesRes.data.employees.forEach(employee => {
                    if (employee.user_id) {
                        userIdToEmployeeId[employee.user_id] = employee.id;
                    }
                });

                // Convert schedules to calendar format
                const formattedSchedules = schedulesRes.data.map(schedule => {
                    const mappedEmployeeId = userIdToEmployeeId[schedule.user_id];
                    const shiftDate = schedule.shift_date.split('T')[0];
                    const startTime = moment(`${shiftDate} ${schedule.start_time}`);
                    const endTime = moment(`${shiftDate} ${schedule.end_time}`);
                    
                    return {
                        id: schedule.id,
                        calendarId: schedule.calendar_type || 'tasks',
                        title: schedule.title || 'Untitled Schedule',
                        start: startTime.toDate(),
                        end: endTime.toDate(),
                        resource: mappedEmployeeId ? String(mappedEmployeeId) : undefined,
                        is_all_day: schedule.is_all_day || false
                    };
                });

                setEmployees(employeesRes.data.employees);
                setSchedules(formattedSchedules);
                setCompanyInfo(companyRes.data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch data');
                setLoading(false);
            }
        };

        fetchData();
        
        // Check if we're returning from a successful employee addition
        const urlParams = new URLSearchParams(window.location.search);
        const refreshEmployees = urlParams.get('refresh');
        if (refreshEmployees === 'true') {
            // Remove the refresh parameter from URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Fetch data again to ensure we have the latest employee list
            setTimeout(fetchData, 1000); // Small delay to ensure backend has processed everything
        }
    }, []);

    const calculateMonthlyHours = (employeeId) => {
        const currentMonth = moment().month();
        const currentYear = moment().year();
        
        const employeeSchedules = schedules.filter(schedule => {
            const scheduleDate = moment(schedule.start);
            return schedule.resource === String(employeeId) &&
                   scheduleDate.month() === currentMonth &&
                   scheduleDate.year() === currentYear;
        });

        const totalHours = employeeSchedules.reduce((total, schedule) => {
            // Only count hours for:
            // 1. Regular work schedules (no countsTowardHours property)
            // 2. PTO requests (countsTowardHours === true)
            // Skip regular time-off requests (countsTowardHours === false)
            if (schedule.hasOwnProperty('countsTowardHours') && !schedule.countsTowardHours) {
                return total; // Skip this event - it's regular time-off, not PTO
            }
            
            const start = moment(schedule.start);
            const end = moment(schedule.end);
            const duration = moment.duration(end.diff(start));
            
            // For all-day events, return 8 hours instead of calculating the full 24 hours
            let hours;
            if (schedule.is_all_day) {
                hours = 8;
            } else {
                hours = duration.asHours();
            }
            
            return total + hours;
        }, 0);

        return Math.round(totalHours);
    };

    const getRandomPastelColor = (seed) => {
        const colors = [
            '#2196F3', // blue
            '#9C27B0', // purple
            '#4CAF50', // green
            '#FF9800', // orange
            '#E91E63', // pink
            '#00BCD4', // cyan
            '#009688', // teal
            '#607D8B'  // blue gray
        ];
        const index = Math.abs(seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % colors.length;
        return colors[index];
    };

    const getBookingImage = (hours) => {
        const bookingPercentage = (hours / 160) * 100;
        
        if (bookingPercentage < 33) {
            return underbooked;
        } else if (bookingPercentage < 66) {
            return booked;
        } else {
            return overbooked;
        }
    };

    const renderAvatar = (employee) => {
        const firstInitial = employee.first_name?.charAt(0)?.toUpperCase() || '';
        const lastInitial = employee.last_name?.charAt(0)?.toUpperCase() || '';
        const initials = firstInitial + lastInitial || employee.email?.charAt(0)?.toUpperCase();
        const backgroundColor = getRandomPastelColor(employee.email);
        const profilePicture = employee.profile_picture_url;
        
        if (profilePicture) {
            return (
                <div 
                    className="employee-avatar" 
                    style={{ backgroundColor: 'transparent', overflow: 'hidden', position: 'relative' }}
                >
                    <img 
                        src={profilePicture} 
                        alt={`${employee.first_name} ${employee.last_name}`} 
                        style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '12px'
                        }}
                        onError={(e) => {
                            console.error('Avatar image failed to load:', profilePicture);
                            // Hide the image and show initials fallback
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling;
                            if (fallback) {
                                fallback.style.display = 'flex';
                            }
                        }}
                    />
                    <div 
                        className="employee-avatar" 
                        style={{ 
                            backgroundColor,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            display: 'none'
                        }}
                    >
                        {initials}
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                className="employee-avatar" 
                style={{ backgroundColor }}
            >
                {initials}
            </div>
        );
    };

    if (loading) {
        return <div className="employees-page loading">Loading...</div>;
    }

    if (error) {
        return <div className="employees-page error">Error: {error}</div>;
    }

    const handleTabClick = (tab, event) => {
        if (event) {
            event.preventDefault();
        }
        setActiveTab(tab);
        if (tab === 'dashboard') {
            window.history.pushState({}, '', '/dashboard');
            window.location.href = '/dashboard';
        } else if (tab === 'requests') {
            window.history.pushState({}, '', '/requests');
            window.location.href = '/requests';
        }
        // Stay on current page for 'employees' tab
    };

    // Add function to handle view toggle
    const handleViewToggle = () => {
        // Get the token from localStorage
        const token = localStorage.getItem('token') || localStorage.getItem('employeeToken');
        
        if (token) {
            // Store the token in both localStorage and sessionStorage for the employee app
            localStorage.setItem('employeeToken', token);
            sessionStorage.setItem('employeeToken', token);
            
            // Navigate to the employee calendar
            window.location.href = 'https://calendar.joinoptivo.com';
        } else {
            console.error('No token found for navigation');
        }
    };

    // Filter employees based on search term and department filters
    const filteredEmployees = employees.filter(employee => {
        // Search term filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = (
                employee.first_name?.toLowerCase().includes(searchLower) ||
                employee.last_name?.toLowerCase().includes(searchLower) ||
                employee.email?.toLowerCase().includes(searchLower) ||
                employee.role?.toLowerCase().includes(searchLower)
            );
            if (!matchesSearch) return false;
        }
        
        // Department filter
        if (visibleDepartments.size > 0) {
            const employeeRole = employee.role || 'Employee';
            if (!visibleDepartments.has(employeeRole)) return false;
        }
        
        return true;
    });

    // Sort employees based on sortOrder
    const sortedEmployees = [...filteredEmployees].sort((a, b) => {
        if (sortOrder === 'highest') {
            const hoursA = calculateMonthlyHours(a.id);
            const hoursB = calculateMonthlyHours(b.id);
            return hoursB - hoursA; // Highest first
        } else if (sortOrder === 'lowest') {
            const hoursA = calculateMonthlyHours(a.id);
            const hoursB = calculateMonthlyHours(b.id);
            return hoursA - hoursB; // Lowest first
        }
        return 0; // Default order
    });

    // Add function to handle add employee
    const handleAddEmployee = async () => {
        if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email) {
            setError('Please fill in all employee information');
            return;
        }

        setIsProcessingPayment(true);
        try {
            // Call the backend API - companyId will be taken from user token
            const response = await api.post('/api/companies/add-employee-checkout', {
                employeeData: newEmployee
            });

            if (response.data.checkoutUrl) {
                // Redirect to Stripe checkout
                window.location.href = response.data.checkoutUrl;
            } else {
                setError('Failed to create checkout session');
                setIsProcessingPayment(false);
            }
        } catch (err) {
            console.error('Add employee error:', err);
            setError(err.response?.data?.error || 'Failed to process payment');
            setIsProcessingPayment(false);
        }
    };

    const getUniqueDepartments = () => {
        const departments = new Set();
        employees.forEach(employee => {
            if (employee.role) {
                departments.add(employee.role);
            }
        });
        return Array.from(departments);
    };

    const getRoleColor = (role) => {
        // Implement your logic to determine the color based on the role
        // For example, you can use a switch statement or a mapping
        switch (role) {
            case 'Manager':
                return '#FF9800';
            case 'HR':
                return '#2196F3';
            case 'Developer':
                return '#4CAF50';
            case 'Designer':
                return '#9C27B0';
            default:
                return '#607D8B';
        }
    };

    const toggleDepartmentVisibility = (department) => {
        const newVisibleDepartments = new Set(visibleDepartments);
        if (newVisibleDepartments.has(department)) {
            newVisibleDepartments.delete(department);
        } else {
            newVisibleDepartments.add(department);
        }
        setVisibleDepartments(newVisibleDepartments);
    };

    return (
        <div className="dashboard">
            <div className="top-header" style={{
                left: '0',  // Override the CSS that sets left: 300px
                paddingLeft: '24px'  // Add padding to match the original design
            }}>
                <h1 style={{ 
                    color: 'white', 
                    fontFamily: 'Albert Sans, sans-serif',
                    fontWeight: '700',
                    fontSize: 'clamp(24px, 4vw, 48px)',
                    margin: '0',
                    position: 'absolute',
                    left: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 100,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100vw - 600px)'
                }}>{companyInfo?.name || 'Loading...'}</h1>
                
                <div style={{ 
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '32px',
                    paddingLeft: '24px'
                }}>
                    {/* Add Employee Button */}
                    <button 
                        className="add-employee-btn"
                        onClick={() => setShowAddEmployeeModal(true)}
                        style={{
                            backgroundColor: 'var(--bg-secondary, #2D2D2D)',
                            color: 'var(--text-primary, white)',
                            border: '1px solid var(--border-color, #404040)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily: 'Inter, sans-serif'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = 'var(--bg-primary, #1E1E1E)';
                            e.target.style.borderColor = 'var(--border-hover, #555555)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'var(--bg-secondary, #2D2D2D)';
                            e.target.style.borderColor = 'var(--border-color, #404040)';
                        }}
                    >
                        + Add Employee
                    </button>

                    {/* Sort Button with Dropdown */}
                    <div className="sort-dropdown-container" style={{ position: 'relative' }}>
                        <button style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#2D2D2D',
                            color: 'rgba(255, 255, 255, 0.6)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#353535'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#2D2D2D'}
                        onClick={() => setShowSortOptions(!showSortOptions)}
                        >
                            Sort{sortOrder === 'highest' ? ' - Most Hours' : sortOrder === 'lowest' ? ' - Least Hours' : ''}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px'
                            }}>
                                <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.01871 9.24934C4.21762 9.24934 4.40839 9.17032 4.54904 9.02967C4.68969 8.88902 4.76871 8.69825 4.76871 8.49934V2.55934L6.73871 4.52934C6.88088 4.66182 7.06893 4.73394 7.26323 4.73052C7.45753 4.72709 7.64292 4.64838 7.78033 4.51096C7.91774 4.37355 7.99646 4.18816 7.99988 3.99386C8.00331 3.79956 7.93119 3.61151 7.79871 3.46934L4.54871 0.21934C4.40808 0.0788896 4.21746 0 4.01871 0C3.81996 0 3.62933 0.0788896 3.48871 0.21934L0.238708 3.46934C0.165021 3.538 0.105919 3.6208 0.0649274 3.7128C0.0239354 3.8048 0.00189351 3.90411 0.000116722 4.00482C-0.00166006 4.10552 0.0168645 4.20555 0.0545855 4.29894C0.0923065 4.39233 0.148451 4.47716 0.21967 4.54838C0.290889 4.6196 0.375723 4.67574 0.469111 4.71346C0.562499 4.75118 0.662528 4.76971 0.763231 4.76793C0.863934 4.76615 0.963247 4.74411 1.05525 4.70312C1.14725 4.66213 1.23005 4.60303 1.29871 4.52934L3.26871 2.55934V8.49934C3.26871 8.91334 3.60471 9.24934 4.01871 9.24934Z" fill="rgba(255, 255, 255, 0.6)"/>
                                </svg>
                                <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(180deg)' }}>
                                    <path d="M4.01871 9.24934C4.21762 9.24934 4.40839 9.17032 4.54904 9.02967C4.68969 8.88902 4.76871 8.69825 4.76871 8.49934V2.55934L6.73871 4.52934C6.88088 4.66182 7.06893 4.73394 7.26323 4.73052C7.45753 4.72709 7.64292 4.64838 7.78033 4.51096C7.91774 4.37355 7.99646 4.18816 7.99988 3.99386C8.00331 3.79956 7.93119 3.61151 7.79871 3.46934L4.54871 0.21934C4.40808 0.0788896 4.21746 0 4.01871 0C3.81996 0 3.62933 0.0788896 3.48871 0.21934L0.238708 3.46934C0.165021 3.538 0.105919 3.6208 0.0649274 3.7128C0.0239354 3.8048 0.00189351 3.90411 0.000116722 4.00482C-0.00166006 4.10552 0.0168645 4.20555 0.0545855 4.29894C0.0923065 4.39233 0.148451 4.47716 0.21967 4.54838C0.290889 4.6196 0.375723 4.67574 0.469111 4.71346C0.562499 4.75118 0.662528 4.76971 0.763231 4.76793C0.863934 4.76615 0.963247 4.74411 1.05525 4.70312C1.14725 4.66213 1.23005 4.60303 1.29871 4.52934L3.26871 2.55934V8.49934C3.26871 8.91334 3.60471 9.24934 4.01871 9.24934Z" fill="rgba(255, 255, 255, 0.6)"/>
                                </svg>
                            </div>
                        </button>

                        {/* Sort Dropdown Menu */}
                        {showSortOptions && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                marginTop: '4px',
                                backgroundColor: '#2D2D2D',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                border: '1px solid #404040',
                                zIndex: 1000,
                                minWidth: '180px',
                                overflow: 'hidden'
                            }}>
                                <div 
                                    style={{
                                        padding: '12px 16px',
                                        color: sortOrder === null ? 'white' : 'rgba(255, 255, 255, 0.7)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: sortOrder === null ? '#404040' : 'transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (sortOrder !== null) e.target.style.backgroundColor = '#353535';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (sortOrder !== null) e.target.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={() => {
                                        setSortOrder(null);
                                        setShowSortOptions(false);
                                    }}
                                >
                                    Default Order
                                </div>
                                <div 
                                    style={{
                                        padding: '12px 16px',
                                        color: sortOrder === 'highest' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: sortOrder === 'highest' ? '#404040' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (sortOrder !== 'highest') e.target.style.backgroundColor = '#353535';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (sortOrder !== 'highest') e.target.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={() => {
                                        setSortOrder('highest');
                                        setShowSortOptions(false);
                                    }}
                                >
                                    Most Hours First
                                </div>
                                <div 
                                    style={{
                                        padding: '12px 16px',
                                        color: sortOrder === 'lowest' ? 'white' : 'rgba(255, 255, 255, 0.7)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        backgroundColor: sortOrder === 'lowest' ? '#404040' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (sortOrder !== 'lowest') e.target.style.backgroundColor = '#353535';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (sortOrder !== 'lowest') e.target.style.backgroundColor = 'transparent';
                                    }}
                                    onClick={() => {
                                        setSortOrder('lowest');
                                        setShowSortOptions(false);
                                    }}
                                >
                                    Least Hours First
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Filter Button */}
                    <div className="filter-dropdown-container" style={{ position: 'relative' }}>
                        <button style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#2D2D2D',
                            color: 'rgba(255, 255, 255, 0.6)',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#353535'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#2D2D2D'}
                        onClick={() => setShowFilterOptions(!showFilterOptions)}
                        >
                            Filter{visibleDepartments.size > 0 ? ` (${visibleDepartments.size})` : ''}
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 4.5C3 4.10218 3.15804 3.72064 3.43934 3.43934C3.72064 3.15804 4.10218 3 4.5 3H19.5C19.8978 3 20.2794 3.15804 20.5607 3.43934C20.842 3.72064 21 4.10218 21 4.5C21 4.89782 20.842 5.27936 20.5607 5.56066C20.2794 5.84196 19.8978 6 19.5 6H4.5C4.10218 6 3.72064 5.84196 3.43934 5.56066C3.15804 5.27936 3 4.89782 3 4.5Z" fill="rgba(255, 255, 255, 0.6)"/>
                                <path d="M6 12C6 11.6022 6.15804 11.2206 6.43934 10.9393C6.72064 10.658 7.10218 10.5 7.5 10.5H16.5C16.8978 10.5 17.2794 10.658 17.5607 10.9393C17.842 11.2206 18 11.6022 18 12C18 12.3978 17.842 12.7794 17.5607 13.0607C17.2794 13.342 16.8978 13.5 16.5 13.5H7.5C7.10218 13.5 6.72064 13.342 6.43934 13.0607C6.15804 12.7794 6 12.3978 6 12Z" fill="rgba(255, 255, 255, 0.6)"/>
                                <path d="M10.5 19.5C10.5 19.1022 10.658 18.7206 10.9393 18.4393C11.2206 18.158 11.6022 18 12 18C12.3978 18 12.7794 18.158 13.0607 18.4393C13.342 18.7206 13.5 19.1022 13.5 19.5C13.5 19.8978 13.342 20.2794 13.0607 20.5607C12.7794 20.842 12.3978 21 12 21C11.6022 21 11.2206 20.842 10.9393 20.5607C10.658 20.2794 10.5 19.8978 10.5 19.5Z" fill="rgba(255, 255, 255, 0.6)"/>
                            </svg>
                        </button>

                        {/* Filter Dropdown Menu */}
                        {showFilterOptions && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                marginTop: '4px',
                                backgroundColor: '#2D2D2D',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                border: '1px solid #404040',
                                zIndex: 1000,
                                minWidth: '100px',
                                maxWidth: '140px',
                                overflow: 'hidden'
                            }}>
                                <div style={{ padding: '4px 0' }}>
                                    <div style={{ padding: '4px 12px', color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        FILTER BY ROLE
                                    </div>
                                    {getUniqueDepartments().map(department => (
                                        <div 
                                            key={department}
                                            style={{
                                                padding: '6px 8px',
                                                color: 'rgba(255, 255, 255, 0.7)',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#353535';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                            onClick={() => toggleDepartmentVisibility(department)}
                                        >
                                            <div 
                                                style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: getRoleColor(department),
                                                    flexShrink: 0
                                                }}
                                            />
                                            <span style={{ fontSize: '11px', fontWeight: '500', flex: 1 }}>{department}</span>
                                            <svg 
                                                width="12" 
                                                height="12" 
                                                viewBox="0 0 24 24" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{ 
                                                    opacity: visibleDepartments.has(department) ? 1 : 0.4,
                                                    transition: 'opacity 0.2s ease',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {visibleDepartments.has(department) ? (
                                                    // Eye open (visible)
                                                    <path d="M12 4.5C7.30558 4.5 3.35558 7.47 2 12C3.35558 16.53 7.30558 19.5 12 19.5C16.6944 19.5 20.6444 16.53 22 12C20.6444 7.47 16.6944 4.5 12 4.5ZM12 17C9.23877 17 7 14.7612 7 12C7 9.23877 9.23877 7 12 7C14.7612 7 17 9.23877 17 12C17 14.7612 14.7612 17 12 17ZM12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9Z" fill="rgba(255, 255, 255, 0.7)"/>
                                                ) : (
                                                    // Eye closed (hidden)
                                                    <path d="M3.53 2.47C3.24 2.18 2.76 2.18 2.47 2.47C2.18 2.76 2.18 3.24 2.47 3.53L4.42 5.48C3.24 6.39 2.25 7.57 1.61 8.97C1.54 9.11 1.5 9.27 1.5 9.43V9.57C1.5 9.73 1.54 9.89 1.61 10.03C2.25 11.43 3.24 12.61 4.42 13.52L6.9 16L8.34 17.44L19.53 6.25C19.82 5.96 19.82 5.48 19.53 5.19C19.24 4.9 18.76 4.9 18.47 5.19L16.76 6.9C15.31 5.96 13.66 5.44 12 5.44C10.34 5.44 8.69 5.96 7.24 6.9L3.53 2.47ZM7.97 8.22C8.6 7.81 9.3 7.6 10.01 7.6C11.11 7.6 12 8.49 12 9.59C12 10.3 11.79 11 11.38 11.63L7.97 8.22ZM16.03 14.78C15.4 15.19 14.7 15.4 13.99 15.4C12.89 15.4 12 14.51 12 13.41C12 12.7 12.21 12 12.62 11.37L16.03 14.78ZM22.39 10.03C21.75 11.43 20.76 12.61 19.58 13.52L18.14 12.08C19.05 11.41 19.83 10.56 20.41 9.57C20.45 9.51 20.45 9.43 20.41 9.37C19.71 8.13 18.7 7.08 17.46 6.36C16.22 5.64 14.8 5.27 13.34 5.27C12.89 5.27 12.45 5.31 12.01 5.38L10.54 3.91C11.02 3.8 11.51 3.74 12 3.74C16.42 3.74 20.25 6.86 22.39 10.03Z" fill="rgba(255, 255, 255, 0.7)"/>
                                                )}
                                            </svg>
                                        </div>
                                    ))}
                                    {getUniqueDepartments().length === 0 && (
                                        <div style={{
                                            padding: '6px 8px',
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            fontSize: '12px',
                                            fontStyle: 'italic'
                                        }}>
                                            No roles found
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* View Toggle Switch - Centered */}
                <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2
                }}>
                    <div style={{
                        width: '200px',
                        height: '44px',
                        backgroundColor: '#2D2D2D',
                        borderRadius: '22px',
                        padding: '4px',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative'
                    }} onClick={handleViewToggle}>
                        <div style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: 'calc(50% - 4px)',
                            height: '36px',
                            backgroundColor: '#1E1E1E',
                            borderRadius: '18px',
                            transition: 'all 0.3s ease',
                            zIndex: 1
                        }}></div>
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            height: '36px',
                            width: '100%',
                            zIndex: 2
                        }}>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Calendar</div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '500',
                                fontSize: '14px',
                                transition: 'color 0.3s ease'
                            }}>Manager</div>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu - Between toggle and search */}
                <div className="sub-nav-menu" style={{ 
                    position: 'absolute',
                    right: '300px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    gap: '32px',
                    zIndex: 2
                }}>
                    <a 
                        href="#" 
                        className={`sub-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={(event) => handleTabClick('dashboard', event)}
                    >
                        Dashboard
                    </a>
                    <a 
                        href="#" 
                        className={`sub-nav-item ${activeTab === 'employees' ? 'active' : ''}`}
                        onClick={(event) => handleTabClick('employees', event)}
                    >
                        Employees
                    </a>
                    <a 
                        href="#" 
                        className={`sub-nav-item ${activeTab === 'requests' ? 'active' : ''}`}
                        onClick={(event) => handleTabClick('requests', event)}
                    >
                        Requests
                    </a>
                </div>

                {/* Team Member Search Box - Top Right Corner */}
                <div className="team-search-box" style={{ 
                    position: 'absolute',
                    right: '24px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '250px',
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-secondary)',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    zIndex: 2
                }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', flexShrink: 0 }}>
                        <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 14L11 11" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input 
                        type="text" 
                        placeholder="Team Member Search" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            border: 'none',
                            outline: 'none',
                            width: '100%',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            fontFamily: 'Inter, sans-serif'
                        }}
                    />
                </div>
            </div>

            <div className="employees-content">
                <div className="employees-grid">
                    {sortedEmployees.map(employee => {
                        const monthlyHours = calculateMonthlyHours(employee.id);
                        return (
                            <div key={employee.id} className="employee-card">
                                <div className="booking-image">
                                    <img 
                                        src={getBookingImage(monthlyHours)} 
                                        alt="Booking level indicator"
                                    />
                                </div>
                                {renderAvatar(employee)}
                                <div className="employee-info">
                                    <h3>{employee.first_name} {employee.last_name}</h3>
                                    <p className="employee-role">{employee.role || 'Employee'}</p>
                                </div>
                                <div className="employee-stats">
                                    <div className="stat">
                                        <span className="stat-label">Monthly Hours</span>
                                        <span className="stat-value">{monthlyHours}/160h</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Tasks</span>
                                        <span className="stat-value">0</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Add Employee Modal */}
            {showAddEmployeeModal && (
                <div className="modal-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: 'var(--bg-primary, #2d2d2d)',
                        borderRadius: '12px',
                        padding: '32px',
                        width: '400px',
                        maxWidth: '90vw'
                    }}>
                        <h2 style={{
                            color: 'var(--text-primary, white)',
                            fontSize: '24px',
                            fontWeight: '600',
                            marginBottom: '24px',
                            textAlign: 'center'
                        }}>Add New Employee</h2>
                        
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                color: 'var(--text-primary, white)',
                                fontSize: '14px',
                                fontWeight: '500',
                                marginBottom: '8px'
                            }}>First Name</label>
                            <input
                                type="text"
                                value={newEmployee.firstName}
                                onChange={(e) => setNewEmployee({...newEmployee, firstName: e.target.value})}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color, #404040)',
                                    backgroundColor: 'var(--bg-secondary, #1e1e1e)',
                                    color: 'var(--text-primary, white)',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter first name"
                            />
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{
                                display: 'block',
                                color: 'var(--text-primary, white)',
                                fontSize: '14px',
                                fontWeight: '500',
                                marginBottom: '8px'
                            }}>Last Name</label>
                            <input
                                type="text"
                                value={newEmployee.lastName}
                                onChange={(e) => setNewEmployee({...newEmployee, lastName: e.target.value})}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color, #404040)',
                                    backgroundColor: 'var(--bg-secondary, #1e1e1e)',
                                    color: 'var(--text-primary, white)',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter last name"
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={{
                                display: 'block',
                                color: 'var(--text-primary, white)',
                                fontSize: '14px',
                                fontWeight: '500',
                                marginBottom: '8px'
                            }}>Email</label>
                            <input
                                type="email"
                                value={newEmployee.email}
                                onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color, #404040)',
                                    backgroundColor: 'var(--bg-secondary, #1e1e1e)',
                                    color: 'var(--text-primary, white)',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                                placeholder="Enter email address"
                            />
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddEmployeeModal(false);
                                    setNewEmployee({ firstName: '', lastName: '', email: '' });
                                    setError('');
                                }}
                                disabled={isProcessingPayment}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--border-color, #404040)',
                                    backgroundColor: 'transparent',
                                    color: 'var(--text-secondary, #9CA3AF)',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                                    opacity: isProcessingPayment ? 0.5 : 1
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddEmployee}
                                disabled={isProcessingPayment}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    backgroundColor: '#1961FD',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: isProcessingPayment ? 'not-allowed' : 'pointer',
                                    opacity: isProcessingPayment ? 0.5 : 1
                                }}
                            >
                                {isProcessingPayment ? 'Processing...' : 'Continue to Payment'}
                            </button>
                        </div>

                        {error && (
                            <div style={{
                                marginTop: '16px',
                                padding: '12px',
                                borderRadius: '6px',
                                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#EF4444',
                                fontSize: '14px',
                                textAlign: 'center'
                            }}>
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Employees; 